import type { RSFundPageModel } from '../../../api/api.types';

export interface MetricData {
  value: number | string | null;
  unit: string;
  limit: string;
  passed: boolean | 'N/A';
  description: string;
}

export interface LiquiditySection {
  daily_maturing_assets: MetricData;
  weekly_maturing_assets: MetricData;
}

export interface MarketRiskSection {
  weighted_average_maturity: MetricData;
  weighted_average_maturity_mmf: MetricData;
  weighted_average_life: MetricData;
  residual_maturity: MetricData;
  duration: MetricData;
}

export interface CreditSection {
  hy_exposure: MetricData;
  weighted_average_credit: MetricData;
}

export interface ConcentrationSection {
  bank_deposits: MetricData;
  abcp_exposure: MetricData;
  corp_exposure: MetricData;
  govt_exposure: MetricData;
}

export interface TimeSeriesMetric {
  '1mo': MetricData;
  '3mo': MetricData;
  '1yr': MetricData;
}

export interface ReturnsData {
  '1yr': number;
  '3mo': number;
  '1mo': number;
  unit: string;
}

export interface VolatilityData {
  '1yr': number;
  '3mo': number;
  '1mo': number;
  unit: string;
}

export interface TimeSeriesAnalysis {
  correlation: TimeSeriesMetric;
  tracking_error: TimeSeriesMetric;
  returns: {
    fund: ReturnsData;
    mmf: ReturnsData;
  };
  volatility: {
    fund: VolatilityData;
    mmf: VolatilityData;
  };
}

export interface Category {
  key: string;
  label: string;
}

export interface Metadata {
  fund_name: string;
  fund_name_full: string;
  position_date: string;
  base_currency: string;
  nav: number;
  mmf_compatibility_score: number;
  mmf_status: string;
  total_criteria: number;
  passed_criteria: number;
  selected_position_date?: string;
  returned_position_date?: string;
}

export interface TimeSeriesChartData {
  series: Array<{
    date: string;
    sub_fund: number;
    mmf_proxy: number;
  }>;
  description: string;
  x_axis: string;
  y_axis: string;
  fund_label: string;
  mmf_label: string;
}

/** Inner `data` payload on the MMF `FundPage`. */
export interface MoneyMarketData {
  metadata: Metadata;
  liquidity: LiquiditySection;
  market_risk: MarketRiskSection;
  credit: CreditSection;
  concentration: ConcentrationSection;
  correlation: TimeSeriesChartData;
  tracking_error: TimeSeriesChartData;
  returns: TimeSeriesChartData;
  time_series_chart_data: TimeSeriesChartData;
}

/** API response for MMF — `RSFundPageModel` from `FundPage.to_dict()`. */
export type MoneyMarketPageModel = RSFundPageModel<MoneyMarketData>;

export interface FinancialMetricsTableData {
  section: string;
  type: string;
  limit: string;
  value: string | number;
  unit: string;
  status: string;
  passed: boolean;
}

export interface HistoricalDataTableData {
  date: string;
  sub_fund: number;
  mmf_proxy: number;
}

export function getMmfReturnedPositionDate(
  pageModel: MoneyMarketPageModel | null | undefined,
): string | null {
  return (
    pageModel?.returned_position_date ??
    pageModel?.data?.metadata?.returned_position_date ??
    null
  );
}

export function getMmfFundNameFull(
  pageModel: MoneyMarketPageModel | null | undefined,
): string | undefined {
  return (
    pageModel?.fund?.fund_name_full ??
    pageModel?.data?.metadata?.fund_name_full
  );
}
